import { useSearchParams } from 'react-router-dom';
import { Badge, Button, Group, Loader, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CityEstablishmentRO } from '@routellers/types/dist/city-establishment/city-establishment.ro';
import { ESTABLISHMENT_KIND_ORDER } from '@routellers/types/dist/city-establishment/enum/establishment-kind.enum';
import { EstablishmentLocaleStatusEnum } from '@routellers/types/dist/city-establishment/enum/establishment-locale-status.enum';
import { ESTABLISHMENT_OFFER_ORDER } from '@routellers/types/dist/city-establishment/enum/establishment-offer.enum';
import {
    ESTABLISHMENT_STATUS_ORDER,
    EstablishmentStatusEnum,
} from '@routellers/types/dist/city-establishment/enum/establishment-status.enum';
import { CONTENT_BASE_LANGUAGE } from '@routellers/types/dist/shared/content-base-language';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { CityQuickSelect, parseCityFilter } from '../components/city-quick-select';
import { EntityThumb } from '../components/entity-thumb';
import { ListPagination } from '../components/list-pagination';
import { PageHeader } from '../components/page-header';
import { EstablishmentEditor } from '../editors/establishment-editor';
import { enumLabel, enumOptions } from '../i18n';
import { coverFromPhotos, LIST_PAGE_SIZE, totalFromMeta } from '../utils/list';

const ALL = 'all';

const statusColor: Record<EstablishmentStatusEnum, string> = {
    [EstablishmentStatusEnum.DRAFT]: 'gray',
    [EstablishmentStatusEnum.PUBLISHED]: 'green',
    [EstablishmentStatusEnum.CLOSED]: 'red',
};

const displayName = (row: CityEstablishmentRO) =>
    row.locales?.find((locale) => locale.language === CONTENT_BASE_LANGUAGE && locale.name)?.name ||
    row.title;

export const EstablishmentsPage = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const [rows, setRows] = useState<CityEstablishmentRO[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [cityFilter, setCityFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState(ALL);
    const [kindFilter, setKindFilter] = useState(ALL);
    const [offerFilter, setOfferFilter] = useState(ALL);
    const [ownerFilter, setOwnerFilter] = useState(ALL);
    const [reviewFilter, setReviewFilter] = useState(ALL);
    const [editorId, setEditorId] = useState<number | null>(() => {
        const id = Number(searchParams.get('edit'));
        return Number.isInteger(id) && id > 0 ? id : null;
    });
    const [creating, setCreating] = useState(false);

    const load = async () => {
        setLoading(true);
        const cityId = parseCityFilter(cityFilter);

        try {
            const where: Record<string, unknown> = {};

            if (cityId) where.cityId = cityId;
            if (statusFilter !== ALL) where.status = statusFilter;
            if (kindFilter !== ALL) where.kind = kindFilter;
            if (offerFilter !== ALL) where.offer = offerFilter;
            if (ownerFilter !== ALL) where.ownerConfirm = ownerFilter;
            if (reviewFilter !== ALL) where.intakeReview = reviewFilter;
            if (!Object.keys(where).length) where.id = null;

            const { data } = await api.cityEstablishmentsApi.query({
                page,
                limit: LIST_PAGE_SIZE,
                searchText: search.trim() || undefined,
                where,
            });

            setRows(data?.items || []);
            setTotal(totalFromMeta(data?.meta, data?.items?.length || 0));
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setPage(1);
    }, [cityFilter, statusFilter, kindFilter, offerFilter, ownerFilter, reviewFilter]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cityFilter, statusFilter, kindFilter, offerFilter, ownerFilter, reviewFilter, page]);

    const kindOptions = enumOptions(ESTABLISHMENT_KIND_ORDER, t, 'enums.establishmentKind');
    const offerOptions = enumOptions(ESTABLISHMENT_OFFER_ORDER, t, 'enums.establishmentOffer');
    const statusOptions = enumOptions(ESTABLISHMENT_STATUS_ORDER, t, 'enums.establishmentStatus');
    const ownerOptions = [
        { value: 'unconfirmed', label: t('pages.establishments.owner.unconfirmed') },
        { value: 'link_sent', label: t('pages.establishments.owner.link_sent') },
        { value: 'confirmed', label: t('pages.establishments.owner.confirmed') },
    ];
    const reviewOptions = [
        { value: 'pending', label: t('pages.establishments.review.pending') },
        { value: 'rejected', label: t('pages.establishments.review.rejected') },
        { value: 'approved', label: t('pages.establishments.review.approved') },
        { value: 'none', label: t('pages.establishments.review.none') },
    ];
    const stamp = (value?: string | null) => {
        if (!value) return '—';

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) return '—';

        return date.toLocaleDateString();
    };

    return (
        <Stack gap='md' style={{ flex: 1, minHeight: 0 }}>
            <PageHeader
                title={t('pages.establishments.title')}
                subtitle={t('pages.establishments.subtitle')}
                action={
                    <Button
                        onClick={() => {
                            setEditorId(null);
                            setCreating(true);
                        }}
                    >
                        {t('pages.establishments.create')}
                    </Button>
                }
            />

            <Group gap='sm' wrap='wrap'>
                <TextInput
                    placeholder={t('pages.establishments.searchPlaceholder')}
                    value={search}
                    onChange={(event) => setSearch(event.currentTarget.value)}
                    onKeyDown={(event) => {
                        if (event.key !== 'Enter') return;
                        if (page !== 1) setPage(1);
                        else load();
                    }}
                    w={260}
                />
                <CityQuickSelect
                    value={cityFilter}
                    onChange={(value) => {
                        setPage(1);
                        setCityFilter(value);
                    }}
                />
                <Select
                    w={160}
                    data={[
                        { value: ALL, label: t('pages.establishments.statusAll') },
                        ...statusOptions,
                    ]}
                    value={statusFilter}
                    onChange={(value) => setStatusFilter(value || ALL)}
                />
                <Select
                    w={180}
                    data={[
                        { value: ALL, label: t('pages.establishments.kindAll') },
                        ...kindOptions,
                    ]}
                    value={kindFilter}
                    onChange={(value) => setKindFilter(value || ALL)}
                />
                <Select
                    w={180}
                    data={[
                        { value: ALL, label: t('pages.establishments.offerAll') },
                        ...offerOptions,
                    ]}
                    value={offerFilter}
                    onChange={(value) => setOfferFilter(value || ALL)}
                />
                <Select
                    w={220}
                    data={[
                        { value: ALL, label: t('pages.establishments.ownerAll') },
                        ...ownerOptions,
                    ]}
                    value={ownerFilter}
                    onChange={(value) => setOwnerFilter(value || ALL)}
                />
                <Select
                    w={180}
                    data={[
                        { value: ALL, label: t('pages.establishments.reviewAll') },
                        ...reviewOptions,
                    ]}
                    value={reviewFilter}
                    onChange={(value) => setReviewFilter(value || ALL)}
                />
            </Group>

            {loading ? (
                <Loader />
            ) : (
                <>
                    <Table.ScrollContainer minWidth={1280}>
                        <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>{t('pages.establishments.columns.name')}</Table.Th>
                                    <Table.Th>{t('pages.establishments.columns.city')}</Table.Th>
                                    <Table.Th>{t('pages.establishments.columns.kind')}</Table.Th>
                                    <Table.Th>{t('pages.establishments.columns.offers')}</Table.Th>
                                    <Table.Th>{t('pages.establishments.columns.guide')}</Table.Th>
                                    <Table.Th>{t('pages.establishments.columns.status')}</Table.Th>
                                    <Table.Th>{t('pages.establishments.columns.owner')}</Table.Th>
                                    <Table.Th>{t('pages.establishments.columns.review')}</Table.Th>
                                    <Table.Th>{t('pages.establishments.columns.visited')}</Table.Th>
                                    <Table.Th>{t('pages.establishments.columns.edited')}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {rows.map((row) => {
                                    const ready = (row.locales || []).filter(
                                        (locale) =>
                                            locale.status === EstablishmentLocaleStatusEnum.READY,
                                    );

                                    return (
                                        <Table.Tr
                                            key={row.id}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => {
                                                setCreating(false);
                                                setEditorId(row.id);
                                            }}
                                        >
                                            <Table.Td>
                                                <EntityThumb
                                                    radius='md'
                                                    src={coverFromPhotos(
                                                        row.photos,
                                                        row.coverPhotoIndex,
                                                    )}
                                                    name={displayName(row)}
                                                    subtitle={
                                                        [row.neighborhood, row.operator]
                                                            .filter(Boolean)
                                                            .join(' · ') || row.slug
                                                    }
                                                />
                                            </Table.Td>
                                            <Table.Td>{row.cityTitle || `#${row.cityId}`}</Table.Td>
                                            <Table.Td>
                                                {enumLabel(t, 'enums.establishmentKind', row.kind)}
                                            </Table.Td>
                                            <Table.Td>
                                                <Group gap={4} wrap='wrap'>
                                                    {(row.offers || []).map((offer) => (
                                                        <Badge
                                                            key={offer}
                                                            variant='light'
                                                            color='gray'
                                                        >
                                                            {enumLabel(
                                                                t,
                                                                'enums.establishmentOffer',
                                                                offer,
                                                            )}
                                                        </Badge>
                                                    ))}
                                                    {!row.offers?.length && (
                                                        <Text size='xs' c='dimmed'>
                                                            —
                                                        </Text>
                                                    )}
                                                </Group>
                                            </Table.Td>
                                            <Table.Td>
                                                <Group gap={4}>
                                                    {ready.map((locale) => (
                                                        <Badge
                                                            key={locale.language}
                                                            variant='light'
                                                        >
                                                            {locale.language}
                                                        </Badge>
                                                    ))}
                                                    {!ready.length && (
                                                        <Text size='xs' c='dimmed'>
                                                            —
                                                        </Text>
                                                    )}
                                                </Group>
                                            </Table.Td>
                                            <Table.Td>
                                                <Badge
                                                    color={statusColor[row.status] || 'gray'}
                                                    variant='light'
                                                >
                                                    {enumLabel(
                                                        t,
                                                        'enums.establishmentStatus',
                                                        row.status,
                                                    )}
                                                </Badge>
                                            </Table.Td>
                                            <Table.Td>
                                                <Badge
                                                    color={
                                                        row.ownerConfirm === 'confirmed'
                                                            ? 'green'
                                                            : row.ownerConfirm === 'link_sent'
                                                              ? 'yellow'
                                                              : 'gray'
                                                    }
                                                    variant='light'
                                                >
                                                    {t(
                                                        `pages.establishments.owner.${row.ownerConfirm || 'unconfirmed'}`,
                                                    )}
                                                </Badge>
                                            </Table.Td>
                                            <Table.Td>
                                                {row.intakeReview && row.intakeReview !== 'none' ? (
                                                    <Badge
                                                        color={
                                                            row.intakeReview === 'pending'
                                                                ? 'orange'
                                                                : row.intakeReview === 'rejected'
                                                                  ? 'red'
                                                                  : 'green'
                                                        }
                                                        variant='light'
                                                    >
                                                        {t(
                                                            `pages.establishments.review.${row.intakeReview}`,
                                                        )}
                                                    </Badge>
                                                ) : (
                                                    <Text size='xs' c='dimmed'>
                                                        —
                                                    </Text>
                                                )}
                                            </Table.Td>
                                            <Table.Td>{stamp(row.lastVisitedAt)}</Table.Td>
                                            <Table.Td>{stamp(row.lastEditedAt)}</Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                                {!rows.length && (
                                    <Table.Tr>
                                        <Table.Td colSpan={10}>
                                            <Text c='dimmed' ta='center' py='md'>
                                                {t('pages.establishments.empty')}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                    <ListPagination page={page} totalItems={total} onChange={setPage} />
                </>
            )}

            <EstablishmentEditor
                opened={creating || editorId != null}
                establishmentId={creating ? null : editorId}
                defaultCityId={parseCityFilter(cityFilter)}
                onClose={() => {
                    setCreating(false);
                    setEditorId(null);
                }}
                onSaved={load}
                onCreated={(id) => {
                    setCreating(false);
                    setEditorId(id);
                }}
                onDeleted={() => {
                    setCreating(false);
                    setEditorId(null);
                    load();
                }}
            />
        </Stack>
    );
};
